/*
 * @Author: Claude
 * @Date: 2025-12-31
 * @Description: List 布局上传组件（音频、混合媒体共用）
 */
import { Upload } from 'antd';
import { FC, ReactElement, ReactNode, useCallback, useEffect, useRef } from 'react';

import { useTranslate } from '@/hooks/useTranslate';
import { AIFunction } from '@/utils/const';

import { MAX_COUNT_DEFAULT } from '../common/constants';
import { useUpload } from '../common/hooks/useUpload';
import { ExtendedUploadFile } from '../common/types';
import { FileUploadRule } from '../common/types/validator';
import { UploadConfig } from '../config/types';

const { Dragger } = Upload;

export interface ListUploadProps {
  /** 文件列表（受控模式） */
  fileList?: ExtendedUploadFile[];
  /** Form.Item 传递的 value */
  value?: ExtendedUploadFile[];
  /** 文件列表变化回调 */
  onChange?: (fileList: ExtendedUploadFile[], file?: ExtendedUploadFile) => void;
  /** 最大文件数量 */
  maxCount?: number;
  /** 校验规则 */
  rules?: FileUploadRule[];
  /** 校验失败回调 */
  onValidationFailed?: (error: string) => void;
  /** 子元素（自定义上传区域） */
  children?: ReactNode;
  /** 文件类型限制 */
  accept?: string;
  /** 上传配置 */
  config: UploadConfig;
  /** 上传功能来源 */
  feature?: AIFunction;
  /** 是否启用埋点 */
  enableTracking?: boolean;
  /** 素材选项名称（用于 GTM resource_option 埋点） */
  resourceOption?: string;
  /** 预览项渲染函数 */
  renderPreviewItem: (
    originNode: ReactElement,
    file: ExtendedUploadFile,
    fileList: ExtendedUploadFile[],
    onDelete: (e: React.MouseEvent) => void,
  ) => ReactNode;
  /** 上传占位符组件 */
  uploadPlaceholder?: ReactNode;
  /** 底部提示文本（可选） */
  footerTip?: string;
  /** 拖拽事件 */
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
}

/**
 * List 布局上传组件
 * 使用列表布局，适用于音频和混合媒体
 */
export const ListUpload: FC<ListUploadProps> = (props) => {
  const {
    children,
    maxCount = MAX_COUNT_DEFAULT,
    onChange,
    fileList: fileListProp,
    value,
    rules,
    onValidationFailed,
    accept,
    config,
    feature,
    enableTracking = true,
    resourceOption,
    renderPreviewItem,
    uploadPlaceholder,
    footerTip,
    onDrop,
    ...restProps
  } = props;

  const { t } = useTranslate();

  // 优先使用 value（Form.Item 模式），否则使用 fileList
  const fileList = value || fileListProp || [];

  // 使用 ref 保存最新的 fileList，解决批量上传的竞态问题
  const fileListRef = useRef<ExtendedUploadFile[]>(fileList);

  useEffect(() => {
    fileListRef.current = fileList;
  }, [fileList]);

  // 文件列表变化的统一处理
  const handleFileListChange = useCallback(
    (updatedFile: ExtendedUploadFile, newFileList: ExtendedUploadFile[]) => {
      // 更新 ref
      fileListRef.current = newFileList;
      // 调用外部回调
      onChange?.(newFileList);
    },
    [onChange],
  );

  // 使用统一的上传 Hook
  const { handleDeleteFile, customRequest } = useUpload({
    fileList,
    fileListRef,
    onFileListChange: handleFileListChange,
    rules,
    onValidationFailed,
    config,
    accept,
    feature,
    enableTracking,
    resourceOption,
  });

  // 自定义渲染每个上传项
  const itemRender = (originNode: ReactElement, file: ExtendedUploadFile) => {
    const onDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleDeleteFile(file);
    };

    return renderPreviewItem(originNode, file, fileList, onDelete);
  };

  return (
    <>
      <div className="border-[1px] rounded-[8px]">
        <Dragger
          className="w-full"
          maxCount={maxCount}
          fileList={fileList}
          itemRender={itemRender}
          customRequest={customRequest}
          showUploadList={fileList.length > 0}
          accept={accept}
          onDrop={onDrop}
          {...restProps}
        >
          {fileList?.length >= maxCount ? null : children || uploadPlaceholder}
        </Dragger>
      </div>
      {footerTip && <span className="text-[10px] text-[#111111]/35">{footerTip}</span>}
    </>
  );
};
